import { AsyncHelper, ResourceModel } from '@bryntum/schedulerpro';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useMaskRenderer } from 'components/organisms/resource-management/scheduler-config/mask-renderer';
import { ViewPresetId } from 'components/organisms/resource-management/scheduler-config/view-presets/constants';
import { SchedulerReference } from 'components/organisms/resource-management/types';
import { useEstimatedBudgetHours } from 'components/organisms/templates/projects/plan/commons/budget-hours/use-estimated-bugdet-hours';
import { getBudgetedHoursConfigFromTemplate } from 'components/organisms/templates/projects/plan/commons/budget-hours/utils';
import { isNonWorkingDay } from 'components/organisms/timesheets/common/utils';
import { DefaultWorkingDayConfig } from 'components/organisms/user-capacity';
import {
  BudgetedHoursCalculationMode,
  useActiveProjectTemplate,
} from 'data/templates/projects';
import { queueMacrotask } from 'utils/macrotask';
import { useAggregationManager } from './aggregration-manager';

interface Props {
  schedulerRef: SchedulerReference;
  relativeDate: Moment;
  onSchedulerReady: () => void;
  projectInfoProvider: () => any;
  isHoursPerWeek: boolean;
}
interface ResultValue {
  isSchedulerInitialized: boolean;
}
export const useSchedulerInitialize = ({
  schedulerRef,
  onSchedulerReady,
  projectInfoProvider,
  isHoursPerWeek,
}: Props): ResultValue => {
  const [isSchedulerInitialized, setSchedulerInitialized] = useState(false);
  const [isSchedulerInitializing, setSchedulerInitializing] = useState(false);
  const { refreshAggregation } = useAggregationManager({
    schedulerRef,
  });
  const template = useActiveProjectTemplate();
  const bugdetedHoursConfig = getBudgetedHoursConfigFromTemplate(template);
  const { totalEfforts } = useEstimatedBudgetHours();

  const { showMask, hideMask } = useMaskRenderer({ schedulerRef });
  const loadSchedulerDefaults = useCallback(async () => {
    showMask();
    await AsyncHelper.sleep(100);

    schedulerRef.current?.instance.suspendRefresh();

    const project = projectInfoProvider();

    if (schedulerRef.current) {
      // eslint-disable-next-line no-param-reassign
      schedulerRef.current.instance.project = project;
      // eslint-disable-next-line no-param-reassign
      schedulerRef.current.instance.viewPreset = isHoursPerWeek
        ? ViewPresetId.WeekZoomed
        : ViewPresetId.DayZoomed;

      if (isHoursPerWeek) {
        schedulerRef.current.instance.timeAxis.filter({
          filters: {
            id: 'non-working-day',
            filterBy: (t) => {
              return !isNonWorkingDay(
                moment(t.startDate),
                DefaultWorkingDayConfig,
              );
            },
          },
          reapplyFilterOnAdd: true,
          reapplyFilterOnUpdate: true,
        });
      }
    }
    queueMacrotask(() => {
      (project.resourceStore?.data as ResourceModel[]).forEach(({ id }) => {
        schedulerRef.current?.instance.expand(id);
        if (bugdetedHoursConfig == null) {
          return;
        }
        const resource =
          schedulerRef.current?.instance.resourceStore.getById(id);

        if (bugdetedHoursConfig.mode === BudgetedHoursCalculationMode.Manual) {
          resource.set('budgetedHoursRange', {
            lowerLimit: bugdetedHoursConfig.value * 60,
            upperLimit: bugdetedHoursConfig.value * 60,
          });
        }
        if (bugdetedHoursConfig.mode === BudgetedHoursCalculationMode.Auto) {
          resource.set('budgetedHoursRange', {
            lowerLimit: totalEfforts.effortsForTasksWithoutDynamicConditions,
            upperLimit: totalEfforts.effortForAllTasks,
          });
        }
      });
    });

    schedulerRef.current?.instance.resumeRefresh(true);
    setSchedulerInitialized(true);
    hideMask();
    refreshAggregation();
    onSchedulerReady();
  }, [
    bugdetedHoursConfig,
    hideMask,
    onSchedulerReady,
    projectInfoProvider,
    refreshAggregation,
    schedulerRef,
    showMask,
    totalEfforts.effortForAllTasks,
    totalEfforts.effortsForTasksWithoutDynamicConditions,
    isHoursPerWeek,
  ]);

  useEffect(() => {
    if (isSchedulerInitialized || isSchedulerInitializing) {
      return;
    }
    setSchedulerInitializing(true);
    loadSchedulerDefaults();
  }, [loadSchedulerDefaults, isSchedulerInitialized, isSchedulerInitializing]);

  return {
    isSchedulerInitialized,
  };
};
